/**
 * exportPdf.ts — Browser print-to-PDF export for VISITOR reports.
 *
 * Opens a new tab with a fully self-contained, print-optimised HTML document.
 * Chart images are captured from live ApexCharts instances registered via
 * registerChartInstance() before the tab opens.
 *
 * Table colours match the live app SCSS:
 *   th normal  : #ECF7FF  ($color4)
 *   th first   : #FAE4ED  ($color8 — the sticky label column)
 *   td border  : #ECF7FF
 *   footer row : #FAE4ED
 */

import { IXlsxReportInput, IXlsxSection, IXlsxTopDay } from './exportXlsx';
import { IReportBreakdownRow } from './reportHelpers';

export type { IXlsxReportInput as IPdfReportInput };

// ─── Chart instance registry ──────────────────────────────────────────────────
// Chart components call registerChartInstance(id, apexInstance) after mount.
// We use this to capture dataURIs without needing window.ApexCharts.

const _chartRegistry = new Map<string, any>();

export function registerChartInstance(id: string, instance: any): void {
    if (id && instance) _chartRegistry.set(id, instance);
}
export function unregisterChartInstance(id: string): void {
    _chartRegistry.delete(id);
}

// ─── Colours (matching app SCSS variables exactly) ───────────────────────────
const BRAND    = '#800000';   // header / section bar
const BRAND_L  = '#A81A40';   // $color6 — accent, footer border, sum border
const TH_BG    = '#ECF7FF';   // $color4 — all th backgrounds
const TH_FIRST = '#FAE4ED';   // $color8 — label-column th + footer row
const TD_BORD  = '#ECF7FF';   // $color4 — cell borders (same as live app)
const FOOT_BG  = '#FAE4ED';   // $color8 — totals row
const CARD_BG  = '#f7f7f8';   // kpi card background
const COLOR2   = '#79869F';   // $color2 — label text colour in body
const COLOR3   = '#8CACC2';   // $color3 — subdued text

// Year colours matching getYearColor() in reportHelpers.ts
const YEAR_COLORS = ['#A81A40', '#E8637A', '#F4A3B2', '#7B8FA1', '#B0BEC5', '#D5DDE1'];
function yearAccent(i: number): string { return YEAR_COLORS[i % YEAR_COLORS.length]; }

// ─── Number helpers ───────────────────────────────────────────────────────────
function fmt(n: number): string { return n.toLocaleString('sk-SK'); }
function fmtPct(pct: number | null): string {
    if (pct === null) return '—';
    return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '\u00a0%';
}
function pctColor(pct: number | null): string {
    if (pct === null) return '#999';
    return pct >= 0 ? '#18B928' : '#C4302F';
}
function yoy(current: number, prev: number): number | null {
    if (prev === 0) return null;
    return ((current / prev) - 1) * 100;
}

// ─── Slovak weekday names ─────────────────────────────────────────────────────
const SK_DAYS = ['Nedeľa', 'Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota'];
function skWeekday(d: string): string { return SK_DAYS[new Date(d + 'T00:00:00').getDay()]; }

// ─── HTML escape ──────────────────────────────────────────────────────────────
function esc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ─── Chart capture ────────────────────────────────────────────────────────────
async function captureChart(chartId: string): Promise<string> {
    const AC = (window as any).ApexCharts;

    // Helper: call dataURI on a raw ApexCharts instance
    async function tryDataUri(inst: any): Promise<string> {
        if (!inst || typeof inst.dataURI !== 'function') return '';
        try {
            // scale:1 keeps the image at the chart's rendered size; the CSS will stretch it to 100% width
            const result = await inst.dataURI({ scale: 1 });
            return result?.imgURI ?? result?.uri ?? '';
        } catch { return ''; }
    }

    // 1. window.ApexCharts.getChartByID — most reliable, works in all versions
    if (AC && typeof AC.getChartByID === 'function') {
        try {
            const inst = AC.getChartByID(chartId);
            const uri = await tryDataUri(inst);
            if (uri) return uri;
        } catch (e) {
            console.warn('[exportPdf] getChartByID failed for', chartId, e);
        }
    }

    // 2. Module-level registry (registered via chart.events.mounted callback)
    const instance = _chartRegistry.get(chartId);
    const uri2 = await tryDataUri(instance);
    if (uri2) return uri2;

    // 3. window.ApexCharts.exec
    if (AC && typeof AC.exec === 'function') {
        try {
            const result = await AC.exec(chartId, 'dataURI', { scale: 1 });
            const uri: string = result?.imgURI ?? '';
            if (uri) return uri;
        } catch (e) {
            console.warn('[exportPdf] ApexCharts.exec failed for', chartId, e);
        }
    }

    // 4. Last resort: SVG serialisation from DOM
    try {
        const el = document.querySelector(`[data-chart-id="${chartId}"] svg, .apexcharts-canvas svg`);
        if (el) {
            const svgStr = new XMLSerializer().serializeToString(el);
            const b64 = btoa(unescape(encodeURIComponent(svgStr)));
            return `data:image/svg+xml;base64,${b64}`;
        }
    } catch { /* ignore */ }

    return '';
}

// ─── HTML fragments ───────────────────────────────────────────────────────────
function sectionBar(title: string): string {
    return `<div class="section-bar">${esc(title)}</div>`;
}

function kpiCard(label: string, value: string, sub?: string, accent = false): string {
    return `<div class="kpi-card${accent ? ' kpi-card--highlight' : ''}">
  <div class="kpi-card__label">${esc(label)}</div>
  <div class="kpi-card__value">${esc(value)}</div>
  ${sub ? `<div class="kpi-card__sub">${esc(sub)}</div>` : ''}
</div>`;
}

function comparisonTable(
    columns: { year: number; label: string }[],
    rows: IReportBreakdownRow[],
): string {
    const showSum = rows.length > 1;
    const sums = columns.map(col =>
        rows.reduce((acc, r) => acc + (r.values.find(v => v.year === col.year)?.value ?? 0), 0)
    );

    // Year column headers — border-bottom uses the year accent colour, matching live getYearColor()
    const headerCols = columns.map((col, i) =>
        `<th style="border-bottom:3px solid ${yearAccent(i)}">${esc(col.label)}</th>`
    ).join('');

    const bodyRows = rows.map((row) => {
        const cells = columns.map((col, ci) => {
            const val = row.values.find(v => v.year === col.year);
            const num = val?.value ?? 0;
            // YoY: col[0] = reference (current/newest). For older columns compare ref vs older.
            const pct = ci > 0
                ? yoy(row.values.find(v => v.year === columns[0].year)?.value ?? 0, num)
                : null;
            const numHtml = num > 0 ? fmt(num) : `<span class="na">N/A</span>`;
            const pctHtml = ci > 0
                ? `<div class="pct" style="color:${pctColor(pct)}">${fmtPct(pct)}</div>`
                : '';
            return `<td><div class="cell-val">${numHtml}</div>${pctHtml}</td>`;
        }).join('');
        return `<tr>
  <td class="label-col"><strong>${esc(row.label)}</strong></td>${cells}
</tr>`;
    }).join('\n');

    const footerRow = showSum ? (() => {
        const cells = sums.map((s, ci) => {
            const pct = ci > 0 ? yoy(sums[0], s) : null;
            const numHtml = s > 0 ? fmt(s) : `<span class="na">N/A</span>`;
            const pctHtml = ci > 0
                ? `<div class="pct" style="color:${pctColor(pct)}">${fmtPct(pct)}</div>`
                : '';
            return `<td><div class="cell-val">${numHtml}</div>${pctHtml}</td>`;
        }).join('');
        return `<tfoot><tr>
  <td class="label-col" style="font-weight:700">Celkom (Σ)</td>${cells}
</tr></tfoot>`;
    })() : '';

    return `<div class="table-wrap">
<table class="vis-table">
  <thead>
    <tr>
      <th class="th-first">Názov</th>${headerCols}
    </tr>
  </thead>
  <tbody>${bodyRows}</tbody>
  ${footerRow}
</table>
</div>`;
}

function topDaysTable(title: string, days: IXlsxTopDay[]): string {
    const sorted = [...days].sort((a, b) => b.total - a.total);
    const rows = sorted.map(({ date, weekday, total }, i) => {
        const dayName = weekday ?? skWeekday(date);
        const jsDay = new Date(date + 'T00:00:00').getDay();
        const isWE = jsDay === 0 || jsDay === 6;
        const rowBg = isWE ? '#FFF8F0' : (i % 2 === 1 ? '#f4fafd' : '#fff');
        // Plain rank number matching the live UI — no medal circles
        const rankHtml = `<span style="color:#666;font-weight:600;font-size:13px">${i + 1}</span>`;
        const d = date.replace(/(\d{4})-(\d{2})-(\d{2})/, '$3.$2.$1');
        return `<tr style="background:${rowBg};-webkit-print-color-adjust:exact;print-color-adjust:exact">
  <td style="text-align:center;padding:10px 12px">${rankHtml}</td>
  <td style="padding:10px 15px"><strong>${d}</strong></td>
  <td style="padding:10px 15px${isWE ? `;color:${BRAND_L};font-weight:600` : ''}">${esc(dayName)}</td>
  <td style="text-align:right;padding:10px 15px"><strong>${fmt(total)}</strong></td>
</tr>`;
    }).join('\n');

    return `${sectionBar(title)}
<div class="table-wrap">
<table class="vis-table">
  <thead>
    <tr>
      <th class="th-first" style="width:60px;text-align:center">#</th>
      <th style="text-align:left">Dátum</th>
      <th style="text-align:left">Deň</th>
      <th style="text-align:right">Návštevnosť</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>
</div>`;
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
function buildCss(): string {
    return `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body {
  font-family: 'Ubuntu','Segoe UI',Tahoma,Arial,sans-serif;
  font-size: 14px; font-weight: 400;
  color: #28475D; background: #fff;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ── Report header ─────────────────────────────────────────────────────────── */
.report-header {
  background: ${BRAND}; color: #fff;
  padding: 26px 34px 20px; page-break-after: avoid;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.report-header .logo-row {
  font-size: 9pt; opacity: 0.65; letter-spacing: 1px;
  text-transform: uppercase; margin-bottom: 14px;
}
.report-header h1 { font-size: 24pt; font-weight: 800; line-height: 1.15; margin-bottom: 4px; }
.report-header .meta-grid {
  display: grid; grid-template-columns: auto 1fr;
  gap: 3px 14px; margin-top: 12px; font-size: 10.5pt;
}
.report-header .meta-lbl { opacity: 0.65; }
.report-header .meta-val { font-weight: 600; }

/* ── KPI strip ─────────────────────────────────────────────────────────────── */
.kpi-strip {
  display: flex; flex-wrap: wrap; gap: 10px;
  padding: 16px 34px; background: ${TH_BG};
  border-bottom: 2px solid #d4e8f5; page-break-inside: avoid;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.kpi-card {
  display: flex; flex-direction: column; gap: 2px;
  padding: 12px 18px; border-radius: 8px;
  background: ${CARD_BG}; border: 1px solid #e0ecf5; min-width: 150px;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.kpi-card--highlight { background: #fff5f5; border-color: #e8b4b8; }
.kpi-card__label {
  font-size: 8.5pt; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.05em; color: ${COLOR3};
}
.kpi-card--highlight .kpi-card__label { color: #c0392b; }
.kpi-card__value { font-size: 19pt; font-weight: 700; color: #222; line-height: 1.1; }
.kpi-card--highlight .kpi-card__value { color: ${BRAND_L}; }
.kpi-card__sub { font-size: 8pt; color: #888; margin-top: 1px; }

/* ── Content wrapper ───────────────────────────────────────────────────────── */
.content { padding: 10px 34px 34px; }

/* ── Section bar ───────────────────────────────────────────────────────────── */
.section-bar {
  background: ${BRAND}; color: #fff;
  font-size: 11.5pt; font-weight: 700;
  padding: 7px 13px; border-radius: 2px;
  margin: 22px 0 10px; page-break-after: avoid;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}

/* ── Section body — constrains both chart and table to the same width ─────── */
.section-body {
  max-width: 900px;
}

/* ── Chart image — fills section-body width, natural aspect ratio ─────────── */
.chart-img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: left center;
  margin-bottom: 16px;
  background: #fff;
}

/* ── Tables — matching live .table SCSS exactly ──────────────────────────── */
.table-wrap { width: 100%; overflow-x: auto; margin-bottom: 20px; }

.vis-table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-size: 12px;
}

/* Header row */
.vis-table th {
  font-weight: 500;
  text-align: center;
  background: ${TH_BG};
  padding: 12px 15px;
  white-space: nowrap;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}

/* First header cell — label column (matching $color8 sticky style) */
.vis-table th.th-first {
  background: ${TH_FIRST};
  text-align: left;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}

/* Last header cell — rounded right corner in live app */
.vis-table th:last-child {
  border-radius: 0 22px 22px 0;
}

/* Body */
.vis-table tbody {
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
}

.vis-table tbody td {
  border-bottom: 1px solid ${TD_BORD};
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.vis-table tbody td:not(:first-child) {
  border-left: 1px solid ${TD_BORD};
}

/* Label column in body — left-aligned, brand text colour */
.vis-table tbody td.label-col,
.vis-table tbody td:first-child {
  white-space: nowrap;
  padding: 15px 15px;
  color: ${COLOR2};
  text-align: left;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}

/* Data cell inner wrapper — mimics .table_drop */
.vis-table .cell-val {
  min-width: 90px;
  padding: 10px 15px 4px;
  font-weight: 500;
  text-align: center;
}

/* Percent row below number */
.pct {
  font-size: 10px;
  font-weight: 600;
  padding: 0 15px 8px;
  text-align: center;
}

/* Footer / totals row */
.vis-table tfoot td {
  background: ${FOOT_BG};
  border-top: 2px solid ${BRAND_L};
  border-bottom: 1px solid ${TD_BORD};
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.vis-table tfoot td:not(:first-child) {
  border-left: 1px solid ${TD_BORD};
}
.vis-table tfoot td.label-col,
.vis-table tfoot td:first-child {
  text-align: left;
  padding: 15px 15px;
  font-weight: 700;
}

.na  { color: #aaa; font-style: italic; font-weight: 400; }

/* ── Page footer ────────────────────────────────────────────────────────────── */
.report-footer {
  margin-top: 34px; padding: 12px 34px;
  border-top: 2px solid ${TH_BG};
  font-size: 8pt; color: #999;
  display: flex; justify-content: space-between;
}

/* ── Print overrides ────────────────────────────────────────────────────────── */
@media print {
  @page { size: A4 landscape; margin: 8mm 10mm; }
  body { font-size: 10px; }
  .report-header { padding: 12px 18px 10px; }
  .report-header h1 { font-size: 16pt; }
  .kpi-strip { padding: 6px 18px; gap: 5px; }
  .kpi-card { min-width: 100px; padding: 6px 10px; }
  .kpi-card__value { font-size: 12pt; }
  .content { padding: 4px 18px 18px; }
  .section-bar { margin: 10px 0 5px; font-size: 9pt; padding: 5px 10px; }
  .vis-table th { font-size: 10px; padding: 8px 10px; }
  .vis-table tbody td { font-size: 10px; }
  .vis-table tbody td:first-child { padding: 10px 10px; }
  .vis-table tfoot td:first-child { padding: 10px 10px; }
  .vis-table .cell-val { padding: 7px 10px 2px; min-width: 70px; }
  .pct { padding: 0 10px 6px; }
  .section-body { max-width: 100%; }
  .section-bar, .vis-table thead { page-break-after: avoid; }
  tr { page-break-inside: avoid; }
}`;
}

// ─── Full HTML ────────────────────────────────────────────────────────────────
function buildHtml(input: IXlsxReportInput, chartImages: Map<string, string>): string {
    const { martName, reportType, dateLabel, weekNum, columns, sections, kpis, topDays, topDaysTitle } = input;
    const trailingSections: IXlsxSection[] = (input as any).trailingSections ?? [];

    const metaRows: Array<[string, string]> = [
        ['Obchodné centrum', martName],
        ['Obdobie', dateLabel],
    ];
    if (weekNum != null) metaRows.push(['Číslo týždňa', String(weekNum)]);

    const metaHtml = metaRows.map(([lbl, val]) =>
        `<span class="meta-lbl">${esc(lbl)}:</span><span class="meta-val">${esc(val)}</span>`
    ).join('\n');

    const kpiHtml = kpis && kpis.length > 0
        ? `<div class="kpi-strip">${(kpis as any[]).map((k, i) =>
            kpiCard(k.label, k.value, k.sub, i === 0)).join('\n')}</div>`
        : '';

    const sectionsHtml = (sections as IXlsxSection[]).map(sec => {
        if (!sec.rows || sec.rows.length === 0) return '';
        const imgHtml = sec.chartId
            ? (() => {
                const uri = chartImages.get(sec.chartId!);
                return uri ? `<img class="chart-img" src="${uri}" alt="${esc(sec.title)}" />` : '';
              })()
            : '';
        return `${sectionBar(sec.title)}\n<div class="section-body">\n${imgHtml}\n${comparisonTable(columns, sec.rows)}\n</div>`;
    }).join('\n');

    const topDaysHtml = topDays && topDays.length > 0
        ? `<div class="section-body">${topDaysTable(topDaysTitle ?? 'Top 10 dní s najvyššou návštevnosťou', topDays as IXlsxTopDay[])}</div>`
        : '';

    const trailingSectionsHtml = trailingSections.map(sec => {
        if (!sec.rows || sec.rows.length === 0) return '';
        const imgHtml = sec.chartId
            ? (() => {
                const uri = chartImages.get(sec.chartId!);
                return uri ? `<img class="chart-img" src="${uri}" alt="${esc(sec.title)}" />` : '';
              })()
            : '';
        return `${sectionBar(sec.title)}\n<div class="section-body">\n${imgHtml}\n${comparisonTable(columns, sec.rows)}\n</div>`;
    }).join('\n');

    const nowStr = new Date().toLocaleString('sk-SK', { dateStyle: 'long', timeStyle: 'short' } as any);

    return `<!DOCTYPE html>
<html lang="sk">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${esc(reportType)} – ${esc(martName)}</title>
  <style>${buildCss()}</style>
</head>
<body>
<header class="report-header">
  <div class="logo-row">VISITOR Analytics &nbsp;·&nbsp; ${esc(reportType)}</div>
  <h1>${esc(martName)}</h1>
  <div class="meta-grid">${metaHtml}</div>
</header>
${kpiHtml}
<main class="content">
${sectionsHtml}
${topDaysHtml}
${trailingSectionsHtml}
</main>
<footer class="report-footer">
  <span>VISITOR Analytics &copy; ${new Date().getFullYear()}</span>
  <span>Vygenerované: ${nowStr}</span>
</footer>
<script>
  window.addEventListener('load', function() { setTimeout(function(){ window.print(); }, 400); });
<\/script>
</body>
</html>`;
}

// ─── Filename helper ──────────────────────────────────────────────────────────
function buildFilename(input: IXlsxReportInput): string {
    const slugify = (s: string) =>
        s.normalize('NFD')
         .replace(/[\u0300-\u036f]/g, '')   // strip diacritics
         .replace(/[^a-zA-Z0-9]+/g, '_')
         .replace(/^_|_$/g, '');
    const mart  = slugify(input.martName  ?? 'report');
    const type  = slugify(input.reportType ?? '');
    const date  = slugify(input.dateLabel  ?? '');
    return [mart, type, date].filter(Boolean).join('_') + '.html';
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Captures all referenced chart images, builds a fully self-contained HTML
 * report and downloads it directly as an .html file.
 * When the user opens the file the embedded <script> auto-triggers
 * window.print() → they choose "Save as PDF" in the browser print dialog.
 */
export async function exportReportPdf(input: IXlsxReportInput): Promise<void> {
    const chartImages = new Map<string, string>();

    // Collect chart images from all sections (including trailing sections)
    const allSections = [
        ...(input.sections as IXlsxSection[]),
        ...((input as any).trailingSections ?? []) as IXlsxSection[],
    ];
    for (const sec of allSections) {
        if (sec.chartId) {
            const uri = await captureChart(sec.chartId);
            if (uri) chartImages.set(sec.chartId, uri);
            else console.warn('[exportPdf] No chart captured for id:', sec.chartId);
        }
    }

    const html     = buildHtml(input, chartImages);
    const blob     = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url      = URL.createObjectURL(blob);
    const filename = buildFilename(input);

    // Direct download — no popup blocker issues, works in all browsers
    const a = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
